import { Link } from "react-router-dom";
import { ArrowLeft, Download, MapPin, Phone, Mail, Globe, ChevronRight, ShieldCheck, Sparkles, ArrowUpRight } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Section";

const FOUNDER_IMG =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/5xrgjpf0_greg%20pic%20smiling%20July%202026%20.jpg";

const LOOP = ["Sense", "Understand", "Prompt", "Act", "Learn"];

const EXPERIENCE = [
  {
    role: "Creative and Multimedia Professional",
    org: null,
    dates: "1995 to Present",
    points: [
      "Holographic presentation development and virtual world building, combining visual storytelling, spatial concepts, and digital experiences.",
      "Effects specialist with experience supporting creative visual presentation and multimedia production.",
      "Documentary director with experience in nonfiction storytelling and visual production.",
      "Director of Photography for Abercrombie & Kent and Starwood Resorts; photography and visual content creation across travel and lifestyle sectors.",
      "Musician and bandleader with more than 30 years of live performance experience; screenwriter developing original narrative projects.",
    ],
  },
  {
    role: "Publisher and Founder",
    org: "Hampton Jitney Magazine",
    dates: "2003 to 2006",
    points: [
      "Founded and operated a regional lifestyle publication; led editorial direction, photography, production, advertising partnerships, and distribution across transportation and hospitality channels.",
    ],
  },
  {
    role: "AI Strategy and Emerging Technology Consultant",
    org: null,
    dates: "2024 to Present",
    points: [
      "Develops approaches for applying AI tools to content creation, product development, creative workflows, and emerging interfaces.",
    ],
  },
  {
    role: "Strategic Consultant",
    org: "Entira Capital",
    dates: "2023 to Present",
    points: [
      "Contributes to conceptual frameworks and advisory discussions involving sovereign finance, capital structuring, aviation, and infrastructure initiatives.",
    ],
  },
  {
    role: "Private Estate Operations and Property Management",
    org: "Nassau, Bahamas",
    dates: "2014 to Present",
    points: [
      "Oversees residential property upkeep, landscaping, irrigation, vendor coordination, repairs, and operational continuity; independently resolves maintenance issues and maintains presentation standards.",
    ],
  },
  {
    role: "Private Estate Management",
    org: "Old Fort Bay Community, Nassau, Bahamas",
    dates: "2006 to 2018",
    points: [
      "Managed property maintenance, routine and emergency repairs, service providers, landscaping, and improvements for a residential property in Nassau, Bahamas.",
    ],
  },
];

const INTERESTS = [
  "Wearable data and behavior",
  "Personalized skincare routines",
  "Longitudinal contextual understanding",
  "AI interfaces",
  "Participant feedback",
  "Formulation access and application habits",
];

const CAPABILITIES = [
  "Invention and product development",
  "Patent development",
  "Creative direction",
  "Holographic presentations",
  "Virtual worlds",
  "Effects production",
  "Documentary directing",
  "Photography",
  "Publishing",
  "AI strategy",
  "Strategic consulting",
  "Vendor and property operations",
];

export default function Resume() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="resume-page">
      <SEO
        title="Gregory Demirjian: Inventor, Founder, Creative Director | WearSpray"
        description="Online résumé for Gregory Demirjian: inventor and founder of WearSpray, a patented wearable dispensing platform, with 20+ years across product innovation, media production, and strategic consulting."
        path="/resume"
      />

      {/* top bar */}
      <div className="flex items-center justify-between px-6 md:px-10 h-16 md:h-20 border-b border-zinc-900 sticky top-0 bg-[#0a0a0a]/90 backdrop-blur z-20">
        <Link to="/" data-testid="resume-back-home" className="flex items-center gap-2.5 group">
          <ArrowLeft size={15} className="text-zinc-500 group-hover:text-accent transition-colors" />
          <span className="font-heading text-sm font-medium tracking-tight text-zinc-400 group-hover:text-white transition-colors">
            Wear<span className="text-accent">Spray</span>
          </span>
        </Link>
        <a
          href="/docs/Gregory_Demirjian_Resume.pdf"
          download="Gregory_Demirjian_Resume.pdf"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="resume-download-pdf"
          className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.15em] uppercase text-zinc-300 border border-zinc-700 rounded-full px-4 py-2 hover:border-accent hover:text-accent transition-colors"
        >
          <Download size={13} />
          <span className="hidden sm:inline">Download PDF</span>
        </a>
      </div>

      {/* hero */}
      <section className="relative overflow-hidden border-b border-zinc-900">
        <div
          className="pointer-events-none absolute -top-32 -left-32 w-[32rem] h-[32rem] rounded-full blur-3xl opacity-25"
          style={{ background: "radial-gradient(circle, #00e5ff, transparent 70%)" }}
        />
        <div
          className="pointer-events-none absolute -bottom-40 -right-24 w-[30rem] h-[30rem] rounded-full blur-3xl opacity-20"
          style={{ background: "radial-gradient(circle, #ff2d78, transparent 70%)" }}
        />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 pt-16 pb-14 md:pt-24 md:pb-20">
          <Reveal>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <img
                src={FOUNDER_IMG}
                alt="Gregory Demirjian"
                className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover border-2 border-zinc-800"
                data-testid="resume-photo"
              />
              <div>
                <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-light tracking-tighter leading-none text-white">
                  Gregory Demirjian
                </h1>
                <p className="font-mono text-[11px] md:text-xs tracking-[0.2em] uppercase text-accent mt-4">
                  Inventor &middot; Founder &middot; Creative Director &middot; Technology Strategist
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-8 font-body text-sm text-zinc-400" data-testid="resume-contact-row">
              <span className="flex items-center gap-1.5">
                <MapPin size={13} className="text-zinc-500" />
                Fairfax Station, VA &amp; Nassau, Bahamas
              </span>
              <a href="tel:+12424310833" className="flex items-center gap-1.5 hover:text-accent transition-colors" data-testid="resume-phone-link">
                <Phone size={13} className="text-zinc-500" />
                242.431.0833
              </a>
              <a href="mailto:greg@wearspray.com" className="flex items-center gap-1.5 hover:text-accent transition-colors" data-testid="resume-email-link">
                <Mail size={13} className="text-zinc-500" />
                greg@wearspray.com
              </a>
              <a href="https://wearspray.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:text-accent transition-colors" data-testid="resume-site-link">
                <Globe size={13} className="text-zinc-500" />
                wearspray.com
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 md:px-10 py-16 md:py-20 space-y-20">
        {/* profile */}
        <Reveal>
          <span className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500 block mb-5">Professional Profile</span>
          <p className="font-body text-zinc-300 text-lg md:text-xl leading-relaxed max-w-3xl">
            Multidisciplinary inventor and founder with more than 20 years of experience across
            product innovation, media production, strategic consulting, and operations. Creator of{" "}
            <Link to="/" className="text-accent hover:underline">WearSpray</Link>, a patented, manufactured
            wearable dispensing platform featured on Shark Tank in 2023. Creative experience spans
            holographic presentations, virtual world building, effects work, documentary directing,
            photography, music, and publishing.
          </p>
          <p className="font-body text-zinc-400 text-lg leading-relaxed max-w-3xl mt-5">
            Research interests center on connecting wearable signals, contextual understanding, and
            personal care behavior through Sense, Understand, Prompt, Act, and Learn. Developing
            concepts that bring together AI, longitudinal understanding of individual routines, and
            accessible physical action.
          </p>

          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-2.5 mt-8" data-testid="resume-loop-chain">
            {LOOP.map((w, i) => (
              <div key={w} className="flex items-center gap-1.5">
                <span className="font-mono text-[11px] sm:text-xs tracking-[0.14em] uppercase text-accent border border-accent/40 bg-accent/[0.07] rounded-full px-3.5 py-1.5">
                  {w}
                </span>
                {i < LOOP.length - 1 && <ChevronRight size={13} className="text-zinc-600 shrink-0" aria-hidden="true" />}
              </div>
            ))}
          </div>
        </Reveal>

        {/* inventions & patents, featured */}
        <Reveal>
          <span className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500 block mb-5">Inventions, Patents &amp; Wearable Innovation</span>
          <div className="border border-accent/30 bg-accent/[0.04] rounded-xl p-6 md:p-8" data-testid="resume-wearspray-card">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="font-heading text-xl md:text-2xl font-medium tracking-tight text-white">
                Founder and Inventor <span className="text-accent">at WearSpray</span>
              </h3>
              <span className="font-mono text-xs tracking-[0.15em] text-zinc-500">2019 to Present</span>
            </div>
            <div className="space-y-2.5 mt-5">
              {[
                "Created a patented, refillable wrist dispenser for personal care formulations, including sunscreen, sanitizer, and insect repellent.",
                "Led product concept development, prototyping, manufacturing strategy, and commercialization; the manufactured product appeared on Shark Tank in 2023.",
                "Developing an AI intelligence layer intended to connect supported wearable data with individual routines, environmental context, and personalized guidance on the cell phone.",
                "Exploring longitudinal analysis of application habits and participant feedback to understand what helps people act on personal care guidance.",
                "Pursuing research and collaboration opportunities with consumer brands, wearable technology organizations, and scientific teams. Connected intelligence and sensor integrations remain in development.",
              ].map((t) => (
                <div key={t} className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
                  <p className="font-body text-zinc-300 text-sm md:text-base leading-relaxed">{t}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 mt-7">
              {[
                ["U.S. Patent Holder", ShieldCheck],
                ["Featured on Shark Tank, 2023", Sparkles],
              ].map(([label, Icon]) => (
                <span key={label} className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.12em] uppercase text-white border border-zinc-700 rounded-full px-3.5 py-2">
                  <Icon size={12} className="text-accent" />
                  {label}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <p className="font-body text-zinc-400 text-sm md:text-base leading-relaxed max-w-3xl">
              Selected invention and concept development: U.S. patent holder for wearable dispensing
              technology, with ongoing work on reservoir formats and integration concepts. Developing{" "}
              <span className="text-white font-medium">My Mood Groove</span>, a patent pending concept
              exploring connections among wearable data, guided awareness, meaningful experiences, and
              personal wellbeing.
            </p>
          </div>
        </Reveal>

        {/* experience timeline */}
        <Reveal>
          <span className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500 block mb-7">Experience</span>
          <div className="space-y-9" data-testid="resume-experience-list">
            {EXPERIENCE.map((e) => (
              <div key={e.role} className="flex gap-5 border-l-2 border-zinc-800 pl-5 py-0.5">
                <div className="flex-1">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <h4 className="font-heading text-base md:text-lg font-medium text-white">
                      {e.role}
                      {e.org && <span className="text-zinc-500 font-normal">, {e.org}</span>}
                    </h4>
                    <span className="font-mono text-[11px] tracking-[0.1em] text-zinc-500 shrink-0">{e.dates}</span>
                  </div>
                  <div className="space-y-2 mt-3">
                    {e.points.map((p) => (
                      <div key={p} className="flex items-start gap-2.5">
                        <span className="w-1 h-1 rounded-full bg-zinc-600 mt-2 shrink-0" />
                        <p className="font-body text-zinc-400 text-sm leading-relaxed">{p}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Reveal>

        {/* R&D interests + capabilities */}
        <div className="grid md:grid-cols-2 gap-12">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500 block mb-5">Research &amp; Development Interests</span>
            <div className="flex flex-wrap gap-2" data-testid="resume-interests">
              {INTERESTS.map((t) => (
                <span key={t} className="font-body text-xs text-zinc-300 border border-zinc-700 rounded-full px-3.5 py-2">
                  {t}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500 block mb-5">Core Capabilities</span>
            <div className="flex flex-wrap gap-2" data-testid="resume-capabilities">
              {CAPABILITIES.map((t) => (
                <span key={t} className="font-body text-xs text-zinc-300 border border-zinc-700 rounded-full px-3.5 py-2">
                  {t}
                </span>
              ))}
            </div>
          </Reveal>
        </div>

        {/* closing */}
        <Reveal>
          <div className="border-t border-zinc-900 pt-10 flex flex-wrap items-center justify-between gap-6">
            <p className="font-body text-zinc-500 text-sm max-w-md">
              Currently building <Link to="/" className="text-accent hover:underline">WearSpray</Link>,
              a patented wearable dispensing platform connecting AI, sensing, and immediate action.
            </p>
            <a
              href="mailto:greg@wearspray.com"
              data-testid="resume-contact-cta"
              className="inline-flex items-center gap-2 font-body text-sm font-medium bg-accent text-black px-6 py-3.5 hover:bg-[#00b3cc] transition-colors shrink-0"
            >
              <Mail size={16} />
              Get in Touch
              <ArrowUpRight size={14} />
            </a>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
